import { shortenNumber, numberToPrettyString } from '@cian/utils';
import { NEW_OBJECTS_NAMES, SHORTEN_NUMBER_OPTIONS, NEW_OBJECTS_KEYS } from '../interfaces/villages';
import { INewobjectCategorySetForListPresenterSchema } from '../../node/repositories/countryside-search/entities/newobject_category/NewobjectCategorySetForListPresenterSchema';
import { IVillageForListPresenterSchema } from '../../node/repositories/countryside-search/entities/village/VillageForListPresenterSchema';
import { ICity } from '../store/geo';
import { getObjectsMaxMinPrice } from './maxMinPrice';

export const NON_BREAKING_SPACE = '\u00A0';

export const INFO_DELIMITER = '・';
export const INFO_DELIMITER_MOBILE = ', ';

export const isArrayWithItems = <T>(value: T) => Array.isArray(value) && value.length > 0;

export const parseStringConfig = (param: string, delimiter = ','): number[] =>
  param.split(delimiter).map(Number).filter(Number);

export const getHeaderArray = (header: string | undefined, defaultValue: number[]): number[] => {
  if (!header) {
    return defaultValue;
  }
  const result = parseStringConfig(header);

  return result.length > 0 ? result : defaultValue;
};

export const checkRegionInArray = (region: ICity | undefined, regions: number[]): boolean => {
  if (!region) {
    return false;
  }
  const currentRegion = region.regionId ? region.regionId : region.id;

  return regions.includes(currentRegion);
};

export const formatNumber = (value: number) =>
  numberToPrettyString(+value.toFixed(2)).replace(/ /g, NON_BREAKING_SPACE);

const getPrice = (price: string) => shortenNumber(Number(price), SHORTEN_NUMBER_OPTIONS);

export const getPriceRange = (priceMin: string, priceMax: string) => {
  let priceMinString = getPrice(priceMin);
  const priceMaxString = getPrice(priceMax);
  const regExp = /.*( тыс| млн| млрд)/;
  const regExpMin = regExp.exec(priceMinString);
  const regExpMax = regExp.exec(priceMaxString);

  if (regExpMin && regExpMax) {
    if (regExpMin[1] === regExpMax[1]) {
      priceMinString = priceMinString.replace(regExpMin[1], '');
    }
    if (regExpMin[0] === regExpMax[0]) {
      return `${priceMaxString} ₽`;
    }
  } else if (priceMinString === priceMaxString) {
    return `${priceMinString} ₽`;
  }

  return `${priceMinString}—${priceMaxString} ₽`;
};

export function rangePrice(min?: number, max?: number): string | undefined {
  const num = min || max;

  if (typeof num !== 'number') {
    return undefined;
  }

  let range;
  if (min !== max) {
    range = [
      min ? `от${NON_BREAKING_SPACE}${formatNumber(min)}` : '',
      min && max ? ' ' : '',
      max ? `до${NON_BREAKING_SPACE}${formatNumber(max)}` : '',
    ].join('');
  } else {
    range = formatNumber(num);
  }

  return `${range}${NON_BREAKING_SPACE}₽`;
}

export const getMobilePriceRange = (village: Pick<IVillageForListPresenterSchema, 'newobjectCategorySet'>) => {
  const { priceMax, priceMin } = getObjectsMaxMinPrice(village);
  if (!Number.isFinite(priceMax) || !Number.isFinite(priceMin)) {
    return '';
  }

  return rangePrice(priceMin, priceMax);
};

export const getAreaBlock = (
  min: number,
  max: number,
  objectType: keyof INewobjectCategorySetForListPresenterSchema,
) => {
  let areaRange = '';

  if (min !== max) {
    areaRange = `от ${formatNumber(Math.trunc(min))} до ${formatNumber(Math.trunc(max))}`;
  } else {
    areaRange = `${formatNumber(Math.trunc(min))}`;
  }
  const postfix = objectType === NEW_OBJECTS_KEYS.LANDPLOT ? 'сот.' : 'м²';

  return `${NEW_OBJECTS_NAMES[objectType]} ${areaRange}${NON_BREAKING_SPACE}${postfix}`;
};

export const getAreaFromBlock = (min: number, max: number, isLandplot?: boolean) => {
  const isEqual = min === max;
  const minPostfix = isLandplot ? 'сот.' : 'м²';

  return `${!isEqual ? 'от ' : ''}${formatNumber(min)} ${minPostfix}`;
};

// Отключаем, т.к. линтер выдаёт Expression is always true
// tslint:disable-next-line:strict-type-predicates
export const isClient = (): boolean => typeof window !== 'undefined';

export const formatPhone = (phone: string): string => {
  let mask = '$1 $2 $3-$4-$5';
  if (/^7\d{10}$/.test(phone)) {
    mask = `+${mask}`;
  }

  return phone.replace(/(\d)(\d{3})(\d{3})(\d{2})(\d{2})/, mask);
};

export const hidePhone = (phone: string): string => {
  return `${phone.slice(0, -2)}...`;
};
